/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import { FileUploadModal, MessengerStatus } from 'components';
import { ImagePlaceholder } from 'components/Icons';
import { apiErrorHandler } from 'api/handler';
import { formatDateTime } from 'utils/dates';
import { uploadBotLogo } from 'services/messenger.service';
import LexBotStatus from 'components/Labels/LexBotStatus';

function FormEditHeader(props) {
  const { formData, setFormData, status } = props;

  const [botAvatarUrl, setBotAvatarUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const [fileUploadModalOptions, setFileUploadModalOptions] = useState({ show: false });
  const [customLoading, setCustomLoading] = useState({
    fileUpload: false,
  });

  useEffect(() => {
    if (formData?.attachments?.botAvatar) {
      const { location } = formData.attachments.botAvatar;
      setBotAvatarUrl(location);
    }
  }, [formData?.attachments]);

  async function uploadLogo(file: any) {
    try {
      setCustomLoading((prevState) => ({ ...prevState, fileUpload: true }));

      // Create FormData and append the selected file
      const payload = new FormData();
      payload.append('file', file);
      const { data } = await uploadBotLogo(formData._id, payload);
      if (data?.node) {
        const { attachment } = data.node;
        const { location } = attachment;
        setBotAvatarUrl(location);
        setFormData((prevState) => ({
          ...prevState,
          attachments: { ...(prevState.attachments || {}), botAvatar: attachment },
        }));
        toast.success('Widget avatar has been updated');
      }
    } catch (e) {
      const { message: exception } = apiErrorHandler(e);
      toast.error(exception);
    } finally {
      setCustomLoading((prevState) => ({ ...prevState, fileUpload: false }));
    }
  }

  function handleOnClickFileUpload() {
    setFileUploadModalOptions({ ...fileUploadModalOptions, show: true });
  }

  const handleCloseFileUploadModal = () => {
    setFileUploadModalOptions({ ...fileUploadModalOptions, show: false });
  };

  // Function to handle image file selection
  const handleOnFileSelected = (file: any) => {
    if (file) {
      // You can perform additional checks/validation on the selected image here
      setSelectedImage(file);
      uploadLogo(file);
    }
    handleCloseFileUploadModal();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          {botAvatarUrl || selectedImage ? (
            <img
              className="w-24 h-24 rounded-full p-2 mr-5 cursor-pointer"
              src={selectedImage ? URL.createObjectURL(selectedImage) : botAvatarUrl}
              alt="CL"
              onClick={handleOnClickFileUpload}
            />
          ) : (
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleOnClickFileUpload}
              aria-disabled={customLoading.fileUpload}
              className="w-20 h-20 rounded-full p-2 mr-5 bg-brightGray flex justify-center items-center cursor-pointer"
            >
              <ImagePlaceholder width={40} height={40} />
            </motion.div>
          )}

          <div className="client-account__details items-center">
            <div className="flex flex-row gap-2 items-center pb-2">
              <h2 className="text-spaceCadetBlue font-bold font-lato text-lg srg:text-xl">
                View Messenger Instance
              </h2>
              <div>
                <MessengerStatus isActive={formData?.isActive || false} />
              </div>
              <div>
                {formData?.instanceType === 'Chatbot' && (
                  <LexBotStatus status={status} isReady={formData.isReady} />
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 items-center">
              <p className="text-spaceCadetBlue font-normal font-lato text-base rg:text-lg">
                Messenger ID : {formData?.sequenceId}
              </p>
              <p className="text-spaceCadetBlue font-normal font-lato text-base rg:text-lg">
                {formData?.companyName}
              </p>

              {formData.createdAt && (
                <div className="text-rasinBlack font-normal font-lato text-sm rg:text-base">
                  Created At: {formatDateTime(formData?.createdAt)}
                </div>
              )}
              {formData.updatedAt && (
                <div className="text-rasinBlack font-normal font-lato text-sm rg:text-base">
                  Last Updated: {formatDateTime(formData?.updatedAt)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {fileUploadModalOptions.show && (
        <FileUploadModal
          show={fileUploadModalOptions.show}
          onClose={handleCloseFileUploadModal}
          onFileSelected={handleOnFileSelected}
          type="image"
          title="Upload Widget avatar"
        />
      )}
    </>
  );
}

FormEditHeader.propTypes = {
  formData: PropTypes.any,
  setFormData: PropTypes.func,
  status: PropTypes.string,
};

export default FormEditHeader;
